import Drawer from 'atoms/Drawer/drawer';
import {
    EVENTS_PAGE_TAB,
    GETTING_STARTED_TAB,
} from 'components/community/constants';
import { CHAR_LIMIT_MAP } from 'components/community/CreateAirmeetForm/CreateAirmeetV2/validations';
import { getEventEntryAccessOptions } from 'components/community/editPublicAirmeet/constants';
import {
    endDateToStartDateTimeDiff,
    getSubstring,
} from 'components/newOnboardingFlow/utils';
import { WELCOME_MODAL_PATH } from 'components/newOnboardingFlow/v3/config';
import addDays from 'date-fns/addDays';
import addMinutes from 'date-fns/addMinutes';
import theme from 'foundations/theme';
import useLeadEvents from 'hooks/leads/useLeadEvents';
import { CREATE_LEAD } from 'hooks/leads/utils';
import useOnboardingAnalytics from 'hooks/onboarding/useOnboardingAnalytics';
import useConferenceTrialLeadHandler from 'hooks/useConferenceTrialLeadHandler';
import useDeviceInfo from 'hooks/useDeviceInfo';
import useTimeZonesValues from 'hooks/useTimezoneValues';
import useToasts from 'hooks/useToasts';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getEventEntrypoints,
    newUpdateAirmeetEntrypoints,
} from 'store/actions/airmeetRules';
import { validationErrors } from 'utils/constants/common';
import { CmDashboardAnalyticsTypes } from 'utils/constants/community/community';
import { DEFAULT_TABLE_COUNT } from 'utils/constants/social-lounge';
import { TOAST_VERSION_2 } from 'utils/constants/toasts';
import { isMaliciousStringPresent, validateMaliciousString } from 'utils/core';
import firebaseLogger, { CmDashboardEvents } from 'utils/firebaseLogger';
import { logger } from 'utils/logger';
import history from '../../../../history';
import {
    createAirmeet,
    editAirmeet,
    fetchAirmeetInfo,
    fetchCreatedAirmeetInfo,
    resetCmDashboardCreatedState,
} from '../../../../store/actions/cmDashboard/airmeet';
import { removeToast } from '../../../../store/actions/toasts';
import { SUMMARY_TAB } from '../../editPublicAirmeet/constants/tabs';
import {
    CONFERENCE_MODE,
    DASHBOARD_INITIATION,
    EVENT_TYPE,
    MEETUP_MODE,
    NEW_ONBOARDING_INITIATION,
} from '../constants';
import {
    validateFormData,
    validateSingleField,
} from '../CreateAirmeetV2/validations';
import useTimeAndDateFormUtils from '../TimeAndDate/useTimeDateFormUtils';
import FormContent from './FormContent';
import { useInitialValues } from './initialValues';
import { getAirmeetDetails } from './utils';

const EVENT_IMAGE_REQUEST_KEY = 'eventImage';

const SOCIAL_LOUNGE_TABLES = {
    EMPTY: 0,
    DEFAULT: DEFAULT_TABLE_COUNT,
};

const EVENT_ENTRY_ACCESS_OPTIONS = getEventEntryAccessOptions();

export const CreateAirmeetV2 = ({
    onClose,
    createMode,
    airmeet,
    airmeetId,
    editMode,
    initiatedFrom = DASHBOARD_INITIATION,
    defaultCommunityId = '',
    hideRightSection = false,
    defaultCommunityName = '',
    pageMode = false,
    submitBtnId = '',
    isFullScreen = false,
    isCreateAirmeetV3 = false,
}) => {
    const { handleCreateTrialLead } = useConferenceTrialLeadHandler();
    const community = useSelector((state) => state.Community.current);
    const { subscription_type } = community || {};
    const [loading, setLoading] = useState(false);
    const [showSidemenu, setShowSideMenu] = useState('');
    const [typeDateTime, setTypeDateTime] = useState('');
    const [selectedOption, setActiveOption] = useState([]);
    const { errorToast, successToast } = useToasts();
    const dispatch = useDispatch();
    const currentToastId = useSelector((state) => get(state, 'toasts[0].id'));
    const initialFormData = useInitialValues(airmeet, community);
    const [eventCreated, setEventCreated] = useState(false);
    const [formData, setFormData] = useState(initialFormData);
    const [analyticsEventsFired, setAnalyticsEventsFired] = useState([]);
    const uploadedImgUrl = useSelector(
        (state) => state.FileUploader.files[EVENT_IMAGE_REQUEST_KEY]
    );
    const { analyticsEventType, logAnalyticsEvent } = useOnboardingAnalytics();
    const timeZone = useTimeZonesValues({ timezone: formData.timeZone });
    const {
        communityId,
        name: community_name,
        manager_id,
        enabled_feature_data,
    } = useSelector((state) => get(state, 'Community.current', {}));
    const entryType = useSelector(
        (state) => state?.cmDashboard?.eventEntryOption?.entryType
    );
    const initialEventEntryValue = useMemo(() => {
        let initialEventEntryValue = null;
        Object.keys(EVENT_ENTRY_ACCESS_OPTIONS).forEach((key) => {
            if (
                get(EVENT_ENTRY_ACCESS_OPTIONS, `${key}.entryType`) ===
                entryType
            ) {
                initialEventEntryValue = get(
                    EVENT_ENTRY_ACCESS_OPTIONS,
                    `${key}.value`
                );
            }
        });
        return editMode ? initialEventEntryValue || 'email' : 'email';
    }, [editMode, entryType]);

    const { is_ugc_enabled: isUGCEnabled } = enabled_feature_data || {};
    const {
        user: { id: uid, first_name: firstName, email },
    } = useSelector(({ auth }) => auth);

    const [eventEntryValue, setEventEntryValue] = useState(
        initialEventEntryValue
    );
    const [domainList, setDomainList] = useState([]);

    const {
        browser: { name: browser },
        isTablet,
        isIOS,
    } = useDeviceInfo();

    const [errors, setErrors] = useState({});
    const nameRef = useRef('');
    const {
        handleLeadOperation,
        eventTypes,
        getLeadDataFromStore,
    } = useLeadEvents();

    const handleNameError = async () => {
        const nameValid = await validateSingleField(
            'name',
            formData.name,
            setErrors,
            false
        );
        !nameValid &&
            nameRef.current.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            });
    };
    const onboardingInitiation = initiatedFrom === NEW_ONBOARDING_INITIATION;
    const { createsEvent } = eventTypes;

    const createEventLeadData = getLeadDataFromStore(createsEvent);

    useEffect(() => {
        handleLeadOperation({ leadName: createsEvent, setLoading });
    }, [createsEvent]);

    useEffect(() => {
        return () => {
            dispatch(resetCmDashboardCreatedState());
        };
    }, [dispatch, communityId]);

    useEffect(() => {
        if (uploadedImgUrl) {
            setFormData((formData) => ({
                ...formData,
                eventImage: uploadedImgUrl.imageURL,
            }));
        }
    }, [uploadedImgUrl]);

    useEffect(() => {
        if (onboardingInitiation) {
            const exploreAirmeet = 'Explores Airmeet';

            setFormData((formData) => ({
                ...formData,
                name: `${getSubstring(
                    firstName,
                    0,
                    CHAR_LIMIT_MAP.name.max - (exploreAirmeet.length + 1)
                )} ${exploreAirmeet}`,
                eventOrganiserName: defaultCommunityName || '',
                initDate: addMinutes(new Date(), 15),
                finalDate: addDays(addMinutes(new Date(), 15), 14),
            }));
        }
    }, [firstName, defaultCommunityName, onboardingInitiation]);

    useEffect(() => {
        if (onboardingInitiation) {
            if (errors?.initDate || errors?.initTime) {
                logAnalyticsEvent(
                    analyticsEventType.createEventStartTimeError,
                    {
                        message: errors?.initDate
                            ? errors?.initDate
                            : errors?.initTime,
                        user_id: uid,
                    }
                );
            }
        }
    }, [
        onboardingInitiation,
        errors?.initDate,
        errors?.initTime,
        analyticsEventType.createEventStartTimeError,
        logAnalyticsEvent,
        uid,
    ]);

    const onValueChange = (key, value) => {
        logger.debug('Value', key, value);
        if (key === 'hasVirtualTables' && !airmeet?.tableCount) {
            setFormData((formData) => ({
                ...formData,
                [key]: value,
                tableCount: value
                    ? SOCIAL_LOUNGE_TABLES.DEFAULT
                    : SOCIAL_LOUNGE_TABLES.EMPTY,
            }));
        } else {
            setFormData((formData) => ({ ...formData, [key]: value }));
        }
    };

    const handleIncrement = () => {
        const { tableCount } = formData;
        if (+tableCount >= 500) {
            return;
        }
        setFormData({ ...formData, tableCount: parseInt(tableCount) + 1 });
        if (editMode && tableCount >= airmeet.tableCount - 1) {
            setErrors({ tableCount: '' });
        }
    };
    const handleDecrement = () => {
        const { tableCount } = formData;
        if (+tableCount === 1) {
            return;
        }
        setFormData({ ...formData, tableCount: parseInt(tableCount) - 1 });
    };
    const changeFormData = async ({ target: { name, value } }) => {
        if (name) {
            if (name === 'tableCount') {
                if (!value) {
                    value = '1';
                } else if (+value > 500) {
                    value = '500';
                }
            }
            setFormData({
                ...formData,
                [name]: value,
            });
            const onboardingFieldCheck =
                name === 'eventOrganiserName' && onboardingInitiation;
            await validateSingleField(
                name,
                value,
                setErrors,
                onboardingFieldCheck
            );
        }
    };

    const updateDateTimeErrorMap = (key, value) => {
        if (value?.status === false)
            setErrors((prevValue) => ({
                ...prevValue,
                [key]: '',
            }));
    };

    const {
        getTzAdjustedPayload,
        onStateChanged,
        getMinTime,
        getMinDate,
    } = useTimeAndDateFormUtils({
        formData,
        setFormData,
        updateErrorMap: onboardingInitiation
            ? updateDateTimeErrorMap
            : () => {},
    });

    const handleNumberInput = (e) => {
        const charCode = e.which ? e.which : e.keyCode;
        if (
            (charCode > 31 && (charCode < 48 || charCode > 57)) ||
            charCode.length > 3
        ) {
            e.preventDefault();
            return false;
        }
        return true;
    };

    const getSessionType = () => {
        switch (createMode) {
            case MEETUP_MODE:
                return EVENT_TYPE.MEETUP;
            case CONFERENCE_MODE:
                return EVENT_TYPE.CONFERENCE;
            default:
                return EVENT_TYPE.DEFAULT;
        }
    };
    const mode = createMode === MEETUP_MODE ? 'Meetup' : 'Conference';
    const modeLabel =
        createMode === MEETUP_MODE ? 'Social Webinar' : 'Conference';
    const eventType = editMode
        ? formData.eventType === 'MEETUP'
            ? 'Social Webinar'
            : 'Conference'
        : null;
    const modeImage =
        createMode === MEETUP_MODE
            ? 'event_type_meetup'
            : 'event_type_conference';
    const modeText =
        createMode === MEETUP_MODE
            ? 'Single track events with unmatched networking'
            : 'Multifaceted experiences that scale to your ambitions';

    const updateEventEntry = async ({ airmeetId }) => {
        if (!isCreateAirmeetV3 && !airmeetId) return;

        const entryPoint =
            domainList.length > 0
                ? { domains: (domainList || []).map((item) => item?.value) }
                : {};
        const entryType = get(
            EVENT_ENTRY_ACCESS_OPTIONS,
            `${eventEntryValue}.entryType`
        );
        const response = await dispatch(
            newUpdateAirmeetEntrypoints({
                airmeetId,
                entryType,
                entryPoint,
            })
        );
        if (response?.error) {
            errorToast('Failed to update event entry changes');
            return;
        } else {
            const response = await dispatch(getEventEntrypoints({ airmeetId }));
            if (response?.error) {
                logger.error(response.error);
            }
        }
    };

    const handleDateTimeError = (isValid) => {
        if (errors?.initDate || errors?.initTime || !isValid) {
            const getElement = document.getElementById('data-time-error');
            getElement.scrollIntoView({
                block: 'center',
                behavior: 'smooth',
            });
        }
    };

    const handleSubmit = async () => {
        if (loading) {
            return;
        }
        if (validateMaliciousData(formData)) {
            return;
        }
        const isValid = await validateFormData(
            formData,
            {
                abortEarly: false,
                context: { timeZone: formData.timeZone },
            },
            setErrors
        );

        let nameValid = true;
        if (onboardingInitiation) {
            nameValid = await validateSingleField(
                'eventOrganiserName',
                formData.eventOrganiserName,
                setErrors,
                onboardingInitiation
            );
        }
        if (!isValid) {
            !showSidemenu && handleNameError();

            handleDateTimeError(isValid);
            return false;
        }

        if (!nameValid) return false;

        setLoading(true);
        if (currentToastId) {
            dispatch(removeToast(currentToastId));
        }
        const { metadata, timeInfo, miscInfo } = getAirmeetDetails({
            formData,
            subscription_type,
            mode,
            getTzAdjustedPayload,
            pageMode,
            onboardingInitiation,
            endDateToStartDateTimeDiff,
        });
        const response = await dispatch(
            createAirmeet({
                communityId: communityId || defaultCommunityId,
                metadata,
                timeInfo,
                miscInfo,
                eventType: getSessionType(),
                eventOrganiserName: formData?.eventOrganiserName,
                supportEmail: formData?.supportEmail,
                enableEmailAuth: true,
            })
        );
        if (!response.error) {
            const { uuid } = await response.payload;
            await updateEventEntry({ airmeetId: uuid });
            if (pageMode && onboardingInitiation) {
                logAnalyticsEvent(
                    analyticsEventType.clickedCreateEventOnboarding
                );
            }
            firebaseLogger.cmDash({
                category: CmDashboardAnalyticsTypes.events,
                event: CmDashboardEvents.Events.event_created,
                eventData: {
                    community_id: communityId,
                    community_name,
                    uid,
                    user_role:
                        uid === manager_id
                            ? 'Community Manager'
                            : 'Event Manager',
                    browser,
                    event_name: formData.name,
                    start_time: formData.initDate,
                    end_time: formData.finalDate,
                    timezone: formData.timeZone,
                    social_lounge_status: formData.hasVirtualTables
                        ? 'enabled'
                        : 'disabled',
                    airmeet_id: uuid,
                },
            });

            if (
                subscription_type !== 'ENTERPRISE' &&
                subscription_type !== 'APPSUMO' && // added this since appsumo users doesnt need conference trial
                getSessionType() === EVENT_TYPE.CONFERENCE
            ) {
                handleCreateTrialLead();
            }

            if (isEmpty(createEventLeadData)) {
                const payload = {
                    leads: [
                        {
                            email: email,
                            name: createsEvent,
                            value: true,
                        },
                    ],
                };
                handleLeadOperation({
                    leadName: createsEvent,
                    payload,
                    operation: CREATE_LEAD,
                });
            }
            dispatch(fetchCreatedAirmeetInfo(uuid));

            if (pageMode) {
                history.push({
                    pathname: `/airmeets/${uuid}/${SUMMARY_TAB}/${WELCOME_MODAL_PATH}`,
                    tab: onboardingInitiation
                        ? GETTING_STARTED_TAB
                        : EVENTS_PAGE_TAB,
                    state: { welcomeModal: true },
                });
            } else {
                history.push(`/airmeets/${uuid}/${SUMMARY_TAB}`, {
                    tab: onboardingInitiation
                        ? GETTING_STARTED_TAB
                        : EVENTS_PAGE_TAB,
                });

                successToast({
                    title: `${modeLabel} created successfully`,
                    message:
                        'You can add additional details to make your event stand out',
                    version: TOAST_VERSION_2,
                });
            }
            setEventCreated(true);
            onClose();
        } else {
            const { code, errors } = response.payload?.json || {};
            let errorMsg = 'Could not create Airmeet, please try again';
            if (isMaliciousStringPresent(code, errors)) {
                errorMsg = validationErrors.RESPONSE_ERROR;
            }
            errorToast(errorMsg);
        }
        setLoading(false);
    };
    const validateMaliciousData = (formData) => {
        let errorMapTemp = {};
        Object.keys(formData).forEach((field) => {
            if (field !== 'longDesc') {
                const isMalicious = validateMaliciousString({
                    value: formData[field],
                    key: field,
                    returnError: false,
                });
                if (isMalicious) {
                    errorMapTemp[field] = validationErrors.FIELD_ERROR;
                }
            }
        });
        if (!isEmpty(errorMapTemp)) {
            setErrors({
                ...errors,
                ...errorMapTemp,
            });
            logger.error('Malicious data present in the form fields');
            return true;
        }
        return false;
    };
    const handleEditSubmit = async () => {
        if (loading) {
            return;
        }
        if (validateMaliciousData(formData)) {
            return;
        }
        const isValid = await validateFormData(
            formData,
            {
                abortEarly: false,
                context: { timeZone: formData.timeZone, editMode },
            },
            setErrors
        );
        logger.error('Errors', JSON.stringify(errors), isValid);
        if (
            formData.tableCount < airmeet.tableCount &&
            formData.hasVirtualTables
        ) {
            setErrors({ tableCount: "You can't reduce the number of tables" });
            return false;
        }
        if (!isValid) {
            return false;
        }
        setLoading(true);
        if (currentToastId) {
            dispatch(removeToast(currentToastId));
        }
        const { metadata, timeInfo, miscInfo } = getAirmeetDetails({
            formData,
            subscription_type,
            mode,
            getTzAdjustedPayload,
            pageMode,
            onboardingInitiation,
            endDateToStartDateTimeDiff,
        });
        const response = await dispatch(
            editAirmeet(airmeetId, {
                communityId,
                metadata,
                timeInfo,
                miscInfo,
                eventOrganiserName: formData?.eventOrganiserName,
                supportEmail: formData?.supportEmail,
            })
        );
        if (!response.error) {
            await updateEventEntry({ airmeetId });
            dispatch(fetchAirmeetInfo(airmeetId));
            let updatedTimeMessage;
            if (new Date(airmeet.start_time).getTime() !== timeInfo.startTime) {
                updatedTimeMessage =
                    'Please review schedule of session(s) as well.';
            }
            successToast({
                title: `${eventType} details edited successfully`,
                message: updatedTimeMessage,
                version: TOAST_VERSION_2,
            });
            onClose();
        } else if (response.error) {
            const { code, message = '', errors } = response.payload?.json || {};
            let errorMsg = message;
            if (isMaliciousStringPresent(code, errors)) {
                errorMsg = validationErrors.RESPONSE_ERROR;
            }
            errorToast({
                title: `Could not update the ${eventType.toLowerCase()}`,
                message: errorMsg,
                version: TOAST_VERSION_2,
            });
        }
        setLoading(false);
    };
    const isFormValid = () => {
        const errorList = Object.keys(errors);
        for (let ind = 0; ind <= errorList.length; ind++) {
            if (errors[errorList[ind]]) {
                return false;
            }
        }
        return true;
    };

    const logOnboardingAnalyticsEvent = (eventType) => {
        // Allow these onboarding events to fire only once per page load
        if (
            onboardingInitiation &&
            pageMode &&
            !analyticsEventsFired.includes(eventType)
        ) {
            logAnalyticsEvent(eventType);
            setAnalyticsEventsFired(analyticsEventsFired.concat([eventType]));
        }
    };

    const drawerTitle = onboardingInitiation
        ? `New Event`
        : editMode
        ? `Edit ${eventType}`
        : !eventCreated
        ? `New ${modeLabel}`
        : '';

    const formContentProps = {
        eventCreated: eventCreated,
        showSidemenu: showSidemenu,
        editMode: editMode,
        handleEditSubmit,
        handleSubmit,
        formData: formData,
        changeFormData: changeFormData,
        nameRef: nameRef,
        mode: mode,
        errors: errors,
        onboardingInitiation: onboardingInitiation,
        pageMode: pageMode,
        setTypeDateTime: setTypeDateTime,
        setShowSideMenu: setShowSideMenu,
        getMinDate: getMinDate,
        getMinTime: getMinTime,
        onStateChanged: onStateChanged,
        typeDateTime: typeDateTime,
        selectedOption: selectedOption,
        handleNameError: handleNameError,
        onValueChange: onValueChange,
        handleNumberInput: handleNumberInput,
        handleIncrement: handleIncrement,
        handleDecrement: handleDecrement,
        setActiveOption: setActiveOption,
        loading: loading,
        isFormValid: isFormValid,
        hideRightSection: hideRightSection,
        modeImage: modeImage,
        modeText: modeText,
        isUGCEnabled: isUGCEnabled,
        submitBtnId: submitBtnId,
        logOnboardingAnalyticsEvent: logOnboardingAnalyticsEvent,
        analyticsEventType: analyticsEventType,
        timeZone: timeZone,
        isFullScreen: isFullScreen,
        isCreateAirmeetV3: isCreateAirmeetV3,
        airmeetId,
        eventEntryValue,
        setEventEntryValue,
        domainList,
        setDomainList,
    };

    return (
        <>
            {pageMode || isFullScreen ? (
                <FormContent {...formContentProps} />
            ) : (
                <Drawer
                    wrapperClosable={false}
                    width={'1062px'}
                    title={drawerTitle}
                    onClose={onClose}
                    headerStyle={{
                        background: onboardingInitiation
                            ? theme.colors.ambience[0]
                            : theme.colors.ambience[21],
                    }}
                    bodyStyle={{
                        background: onboardingInitiation
                            ? theme.colors.ambience[0]
                            : theme.colors.ambience[21],
                    }}
                    showClose
                    height={
                        onboardingInitiation && isTablet
                            ? isIOS
                                ? '100%'
                                : '91vh'
                            : '100vh'
                    }
                >
                    <FormContent {...formContentProps} />
                </Drawer>
            )}
        </>
    );
};
